import React, { useEffect, useRef } from 'react';
import {
  Box,
  Dialog,
  Flex,
  Portal,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { MdClose } from 'react-icons/md';
import { useAuth } from '../../hooks/useAuth';
import { useKeyboardBottomInset } from '../../hooks/useKeyboardBottomInset';
import {
  SAFE_AREA_INSET_BOTTOM,
  SAFE_AREA_INSET_LEFT,
  SAFE_AREA_INSET_RIGHT,
  SAFE_AREA_INSET_TOP,
} from '../layout/layoutConstants';
import { SHEET_Z_DECK_DIALOG } from '../ui/bottom-sheet';
import { RiftboundIconButton } from '../ui/icon-button';
import { riftboundToaster } from '../ui/toaster';
import { SignInHeader } from './SignInHeader';
import { SignInFooter } from './SignInFooter';
import { EmailPasswordForm } from './EmailPasswordForm';

interface RiftboundSignInProps {
  open: boolean;
  title?: string | null;
  onClose: () => void;
}

/** Above card/filter sheets so gated actions can open auth over them. */
const SIGN_IN_COVER_Z = SHEET_Z_DECK_DIALOG + 40;

const coverTransition = {
  duration: 0.32,
  ease: [0.32, 0.72, 0, 1] as const,
};

type SignInPresentation = 'mobile' | 'desktop';

function clearBodyInteractionLocks() {
  // Zag/Chakra Dialog can leave these behind if it unmounts without a clean close
  // (e.g. switching from dialog → mobile cover after a breakpoint resolves).
  document.body.style.pointerEvents = '';
  document.body.style.overflow = '';
  document.documentElement.style.pointerEvents = '';
  document.documentElement.style.overflow = '';
}

export const RiftboundSignIn: React.FC<RiftboundSignInProps> = ({ open, title, onClose }) => {
  const { signInWithProvider, signInWithCredentials, signUpWithCredentials, forgotPassword, user } = useAuth();
  // undefined until Chakra measures the viewport — do not default to desktop on phones.
  const breakpointMobile = useBreakpointValue({ base: true, md: false });
  const presentationRef = useRef<SignInPresentation | null>(null);
  const openRef = useRef(open);
  openRef.current = open;
  const prefersReducedMotion = useReducedMotion();

  // Lock presentation for the lifetime of an open session so we never mount a
  // Chakra Dialog and then tear it down for the mobile cover (Zag scroll-lock leak).
  if (open && breakpointMobile !== undefined && presentationRef.current === null) {
    presentationRef.current = breakpointMobile ? 'mobile' : 'desktop';
  }

  const presentation = presentationRef.current;
  const isMobile = presentation === 'mobile';
  const keyboardInset = useKeyboardBottomInset(Boolean(open && isMobile));

  useEffect(() => {
    if (user && open) onClose();
  }, [user, open, onClose]);

  // Full-screen cover owns the viewport — lock underlay scroll while open.
  useEffect(() => {
    if (!open || !isMobile) return undefined;
    clearBodyInteractionLocks();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
      clearBodyInteractionLocks();
    };
  }, [isMobile, open]);

  useEffect(() => {
    if (!open || !isMobile) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isMobile, onClose, open]);

  const handleProvider = async (provider: string) => {
    try {
      await signInWithProvider(provider);
      onClose();
    } catch (err) {
      riftboundToaster.create({
        title: 'Sign-in failed',
        description: err instanceof Error ? err.message : 'Please try again.',
        type: 'error',
      });
    }
  };

  const handleEmailSignIn = async (email: string, password: string) => {
    await signInWithCredentials(email, password);
    onClose();
  };

  const handleEmailSignUp = async (email: string, password: string, name: string) => {
    await signUpWithCredentials(email, password, name);
    onClose();
  };

  const handleForgotPassword = async (email: string) => {
    await forgotPassword(email);
    riftboundToaster.create({ title: 'Email sent!', description: 'Check your inbox for the reset link.', type: 'success' });
  };

  // Closed, or open but still waiting for the breakpoint measurement.
  if (presentation === null) return null;

  const formContent = (
    <>
      <SignInHeader isMobile={isMobile} title={title} />
      <EmailPasswordForm
        onSignIn={handleProvider}
        onEmailSignIn={handleEmailSignIn}
        onEmailSignUp={handleEmailSignUp}
        onForgotPassword={handleForgotPassword}
        providersFirst={isMobile}
      />
      <SignInFooter isMobile={isMobile} />
    </>
  );

  if (isMobile) {
    return (
      <AnimatePresence
        onExitComplete={() => {
          if (!openRef.current) presentationRef.current = null;
        }}
      >
        {open ? (
          <motion.div
            key="riftbound-sign-in-cover"
            role="dialog"
            aria-modal="true"
            aria-label="Sign in"
            initial={prefersReducedMotion ? { opacity: 0 } : { y: '100%' }}
            animate={prefersReducedMotion ? { opacity: 1 } : { y: 0 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { y: '100%' }}
            transition={coverTransition}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: SIGN_IN_COVER_Z,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Flex
              direction="column"
              flex="1"
              minH={0}
              bg="bg"
              pt={`calc(0.25rem + ${SAFE_AREA_INSET_TOP})`}
              pl={SAFE_AREA_INSET_LEFT}
              pr={SAFE_AREA_INSET_RIGHT}
            >
              <Flex
                align="center"
                justify="space-between"
                px={2}
                minH="44px"
                flexShrink={0}
              >
                <RiftboundIconButton
                  aria-label="Close"
                  variant="ghost"
                  size="sm"
                  color="text"
                  onClick={onClose}
                >
                  <MdClose size={22} />
                </RiftboundIconButton>
                <Text
                  fontFamily="heading"
                  fontWeight="semibold"
                  fontSize="17px"
                  color="text"
                  truncate
                >
                  Sign in
                </Text>
                {/* Balance the close control so the title stays centered. */}
                <Box w="44px" flexShrink={0} aria-hidden />
              </Flex>

              <Box
                flex="1"
                minH={0}
                overflowY="auto"
                overscrollBehavior="contain"
                px={6}
                pt={2}
                pb={`calc(1.5rem + ${SAFE_AREA_INSET_BOTTOM} + ${keyboardInset}px)`}
              >
                <Flex direction="column" gap={4} maxW="400px" mx="auto" w="100%">
                  {formContent}
                </Flex>
              </Box>
            </Flex>
          </motion.div>
        ) : null}
      </AnimatePresence>
    );
  }

  if (!open) {
    presentationRef.current = null;
    return null;
  }

  return (
    <Dialog.Root open={open} size="sm" placement="center" onOpenChange={e => { if (!e.open) onClose(); }}>
      <Portal>
        <Dialog.Backdrop bg="blackAlpha.700" style={{ backdropFilter: 'blur(4px)' }} />
        <Dialog.Positioner>
          <Dialog.Content
            bg="bg"
            borderRadius="xl"
            border="hairline"
            borderColor="border"
            style={{ boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}
          >
            <Dialog.Body
              display="flex"
              flexDirection="column"
              p={7}
              gap={2}
            >
              {formContent}
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
